/**
 * Soren Today View — Persistent daily command center.
 * Features:
 * 1. Daily cash briefing card (bank, expected in, lowest point, sparkline, verdict, Open 8-week detail / Update bank)
 * 2. Needs attention (highest-consequence-first from ranked cards)
 * 3. Quiet for now (grouped low-priority signals)
 */

import { useEffect, useRef, useState } from 'react'
import type { RankSorenResult, SorenCard } from './rankSorenCards'
import SorenCardView from './SorenCardView'
import SorenDayOneChecklist from './SorenDayOneChecklist'

export type BriefingStatus = 'loading' | 'unauthorized' | 'error' | 'unset' | 'ready'

export interface CashForecastBriefing {
  bankAmount: number | null
  bankFormatted: string
  bankAsOf: string | null
  expectedInAmount: number
  expectedInFormatted: string
  lowestPointAmount: number
  lowestPointFormatted: string
  lowestWeek: string | null
  verdictLine: string
  confidence: string
  weekValues: number[]
  weekLabels: string[]
  weekIn?: number[]
  weekOut?: number[]
  committedOutFormatted?: string
}

interface SorenTodayViewProps {
  ranked: RankSorenResult
  briefing: CashForecastBriefing | null
  briefingStatus: BriefingStatus
  fetchFailed?: boolean
  onRetryCash?: () => void
  onRetryItems?: () => void
  onOpenCashDetail: () => void
  onUpdateBank: () => void
  onOpenCardHref: (href: string) => void
  onCardDone: (card: SorenCard) => void
  onCardRemind: (card: SorenCard) => void
  onCardHide: (card: SorenCard) => void
}

function drawSparkline(canvas: HTMLCanvasElement | null, values: number[]) {
  if (!canvas || !canvas.offsetWidth || values.length === 0) return
  const dpr = window.devicePixelRatio || 1
  const w = canvas.offsetWidth
  const h = canvas.offsetHeight
  canvas.width = w * dpr
  canvas.height = h * dpr
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.scale(dpr, dpr)
  ctx.clearRect(0, 0, w, h)

  const p = { l: 8, r: 8, t: 8, b: 8 }
  const minVal = Math.min(-1000, ...values)
  const maxVal = Math.max(10000, ...values)
  const x = (i: number) => p.l + (i * (w - p.l - p.r)) / Math.max(1, values.length - 1)
  const y = (v: number) => p.t + ((maxVal - v) * (h - p.t - p.b)) / Math.max(1, maxVal - minVal)

  // Subtle grid lines
  ctx.strokeStyle = '#E4E4E0'
  ctx.lineWidth = 1
  ;[0.25, 0.5, 0.75].forEach((r) => {
    const yy = p.t + (h - p.t - p.b) * r
    ctx.beginPath()
    ctx.moveTo(p.l, yy)
    ctx.lineTo(w - p.r, yy)
    ctx.stroke()
  })

  // Zero line if crossing
  if (minVal < 0 && maxVal > 0) {
    ctx.setLineDash([3, 3])
    ctx.strokeStyle = '#B9BBB6'
    ctx.beginPath()
    ctx.moveTo(p.l, y(0))
    ctx.lineTo(w - p.r, y(0))
    ctx.stroke()
    ctx.setLineDash([])
  }

  // Value curve
  ctx.beginPath()
  values.forEach((v, i) => {
    if (i === 0) ctx.moveTo(x(i), y(v))
    else ctx.lineTo(x(i), y(v))
  })
  ctx.strokeStyle = '#3D6B4F'
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'
  ctx.stroke()

  // Value markers
  values.forEach((v, i) => {
    ctx.beginPath()
    const isLowest = v === Math.min(...values)
    ctx.arc(x(i), y(v), isLowest ? 3.8 : 2.5, 0, Math.PI * 2)
    ctx.fillStyle = v < 0 ? '#9A3B2E' : '#FFFFFF'
    ctx.fill()
    ctx.strokeStyle = v < 0 ? '#9A3B2E' : '#3D6B4F'
    ctx.lineWidth = 1.5
    ctx.stroke()
  })
}

export default function SorenTodayView({
  ranked,
  briefing,
  onOpenCashDetail,
  onUpdateBank,
  onOpenCardHref,
  onCardDone,
  onCardRemind,
  onCardHide,
  briefingStatus,
  fetchFailed,
  onRetryCash,
  onRetryItems,
}: SorenTodayViewProps) {
  const [quietOpen, setQuietOpen] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    if (briefingStatus !== 'ready' || !briefing?.weekValues?.length) return
    const handle = requestAnimationFrame(() => {
      drawSparkline(canvasRef.current, briefing.weekValues)
    })
    return () => cancelAnimationFrame(handle)
  }, [briefingStatus, briefing?.weekValues])

  const attentionCards = [...ranked.doFirst, ...ranked.thenReview]
  const isLowestNegative = (briefing?.lowestPointAmount ?? 0) < 0

  return (
    <section className="tab-view active" id="todayView" data-view="today" data-testid="soren-today-view">
      <SorenDayOneChecklist onOpenHref={onOpenCardHref} />

      <div className="section-label">
        <span>Daily cash briefing</span>
        <span>Updated today</span>
      </div>

      {briefingStatus === 'error' ? (
        <article className="card cash-summary" data-testid="soren-cash-error-card">
          <div className="card-head">
            <div>
              <span className="kicker">Cash briefing</span>
              <h2>Couldn't load your cash briefing</h2>
            </div>
          </div>
          <p style={{ margin: '8px 0 16px', color: 'var(--ink3, #666)', fontSize: 13, lineHeight: 1.5 }}>
            We were unable to reach the cash intelligence service. Please check your connection and try again.
          </p>
          <div className="actions">
            <button
              className="btn primary"
              type="button"
              data-testid="soren-retry-cash-btn"
              onClick={onRetryCash}
            >
              Retry
            </button>
          </div>
        </article>
      ) : briefingStatus === 'unset' ? (
        <article className="card cash-summary" data-testid="soren-cash-unset-card">
          <div className="card-head">
            <div>
              <span className="kicker">Cash forecast</span>
              <h2>Set your bank balance to see this</h2>
            </div>
          </div>
          <p style={{ margin: '8px 0 16px', color: 'var(--ink3, #666)', fontSize: 13, lineHeight: 1.5 }}>
            Enter your current bank balance in Cash Settings to unlock your 8-week cash forecast and daily cash briefing.
          </p>
          <div className="actions">
            <button
              className="btn primary"
              type="button"
              data-testid="soren-set-bank-btn"
              onClick={onUpdateBank}
            >
              Set bank balance
            </button>
          </div>
        </article>
      ) : briefingStatus === 'loading' ? (
        <article className="card cash-summary" data-testid="soren-cash-loading-card">
          <div className="card-head">
            <div>
              <span className="kicker">Cash briefing</span>
              <h2>Loading your cash briefing…</h2>
            </div>
          </div>
        </article>
      ) : briefing ? (
        <article className="card cash-summary" data-testid="soren-cash-briefing-card">
          <div className="card-head">
            <div>
              <span className="kicker">Cash today + next 8 weeks</span>
              <h2>
                {briefing.lowestWeek
                  ? `Lowest point ${briefing.lowestPointFormatted} week of ${briefing.lowestWeek}.`
                  : 'Cash forecast ready.'}
              </h2>
            </div>
            <span className="confidence" data-testid="soren-briefing-confidence">{briefing.confidence}</span>
          </div>

          <div className="metric-grid" data-testid="soren-cash-strip">
            <div className="metric">
              <span>Bank balance</span>
              <strong className="money" data-testid="soren-bank-balance">
                {briefing.bankFormatted}
              </strong>
              <small data-testid="soren-bank-as-of">{briefing.bankAsOf ? `as of ${briefing.bankAsOf}` : 'not connected'}</small>
            </div>
            <div className="metric">
              <span>Expected in</span>
              <strong className="money" data-testid="soren-expected-in">
                {briefing.expectedInFormatted}
              </strong>
              <small>next 30 days</small>
            </div>
            <div className="metric">
              <span>Lowest point</span>
              <strong
                className={`money ${isLowestNegative ? 'negative' : ''}`}
                data-testid="soren-lowest-point"
              >
                {briefing.lowestPointFormatted}
              </strong>
              <small data-testid="soren-lowest-week">{briefing.lowestWeek ? `week of ${briefing.lowestWeek}` : 'projected'}</small>
            </div>
          </div>

          <div className="spark">
            <canvas
              ref={canvasRef}
              id="dailyChart"
              data-testid="soren-daily-chart"
              aria-label="Eight week cash forecast sparkline"
            />
            {briefing.weekLabels.length > 0 && (
              <div className="spark-labels">
                {briefing.weekLabels.map((lbl, i) => (
                  <span key={i}>{lbl}</span>
                ))}
              </div>
            )}
          </div>

          <div className="cash-verdict" data-testid="soren-cash-verdict">
            <b>{briefing.verdictLine}</b> Soren will keep watching this and update Today when the numbers change.
          </div>

          <div className="actions">
            <button
              className="btn primary"
              type="button"
              data-open-answer="cash"
              data-testid="soren-open-cash-detail"
              onClick={onOpenCashDetail}
            >
              Open 8-week detail
            </button>
            <button
              className="btn"
              type="button"
              data-action="update-bank"
              data-testid="soren-update-bank"
              onClick={onUpdateBank}
            >
              Update bank
            </button>
          </div>
        </article>
      ) : null}

      {fetchFailed ? (
        <>
          <div className="section-label">
            <span>Needs attention</span>
            <span>Unable to refresh</span>
          </div>
          <article className="card soren-fetch-error-card" data-testid="soren-fetch-error-card" style={{ padding: '20px' }}>
            <div className="card-head">
              <div>
                <span className="kicker">Items</span>
                <h2>Couldn't load your items</h2>
              </div>
            </div>
            <p style={{ margin: '8px 0 16px', color: 'var(--ink3, #666)', fontSize: 13, lineHeight: 1.5 }}>
              There was a problem loading your action items and notifications. Please check your connection and try again.
            </p>
            <div className="actions">
              <button
                type="button"
                className="btn primary"
                data-testid="soren-retry-items-btn"
                onClick={onRetryItems}
              >
                Retry
              </button>
            </div>
          </article>
        </>
      ) : (
        <>
          <div className="section-label">
            <span>Needs attention</span>
            <span>
              {attentionCards.length === 1
                ? '1 item, highest consequence first'
                : `${attentionCards.length} items, highest consequence first`}
            </span>
          </div>

          {attentionCards.map((card) => (
            <SorenCardView
              key={card.id}
              card={card}
              onOpen={onOpenCardHref}
              onDone={() => onCardDone(card)}
              onRemind={() => onCardRemind(card)}
              onHide={() => onCardHide(card)}
            />
          ))}

          <div className="section-label">
            <span>Quiet for now</span>
            <span>Watched, not interrupting</span>
          </div>
          <div className="quiet" data-testid="soren-quiet">
            <div className="quiet-row">
              <span>{ranked.quietGroup.summary}</span>
              {ranked.quietGroup.detailLines.length > 0 && (
                <button
                  className="btn"
                  id="quietToggle"
                  data-testid="soren-quiet-toggle"
                  type="button"
                  onClick={() => setQuietOpen((o) => !o)}
                >
                  {quietOpen ? 'Hide' : 'View'}
                </button>
              )}
            </div>
            {quietOpen && ranked.quietGroup.detailLines.length > 0 && (
              <div className="quiet-details show" data-testid="soren-quiet-more">
                {ranked.quietGroup.detailLines.join(' · ')}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  )
}
